import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { isNonEmptyString } from "@insite/client-framework/Common/Utilities/StringUtilities";
import {
    HasProduct,
    HasProductContext,
    ProductContext,
    withProduct,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";

import siteMessage from "@insite/client-framework/SiteMessage";
import freightQuoteHandler from "@tCentro/Handlers/CENPDPFreightQuoteHandler";

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import displayProduct from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import translate from "@insite/client-framework/Translate";
import { BaseAddressModel, CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import usePaymentDetailsCartState from "@insite/content-library/Hooks/Payments/usePaymentDetailsCartState";
import usePaymentDetailsFormValidation from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormValidation";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENErrorIcon from "@tCentro/Icons/CENErrorIcon";
import CenFreightIcon from "@tCentro/Icons/CenFreightIcon";
import CenQuestionIcon from "@tCentro/Icons/CenQuestionIcon";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";

import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import Img from "@insite/mobius/Img";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import { getProp } from "@insite/mobius/utilities";
import cenQtyMin from "@tCentro/Handlers/CenQtyMinHandler";
import cenQtyMulti from "@tCentro/Handlers/CenQtyMultiHandler";
import CustomInfoIcon from "@tCentro/Icons/CustomInfoIcon";
import ProductAddToCartButton, { InfoIconTooltipStyles } from "@tCentro/Overrides/Components/ProductAddToCartButton";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import getSmartyAutocomplete from "@tCentro/Handlers/CENSmartyAutocomplateHandler";
import debounce from "lodash/debounce";

const enum fields {
    freightlink = "freightlink",
    freightContent = "freightContent",
    phoneNo = "phoneNo",
    phoneContent = "phoneContent",
    phoneHeading = "phoneHeading",
    backgroundColor = "backgroundColor",
    freightContentColor = "freightContentColor",
    headingColor = "headingColor",
    shippingRateBackgroundColor = "shippingRateBackgroundColor",
    logisticImage1 = "logisticImage1",
    logisticImage1Alt = "logisticImage1Alt",
    logisticImage2 = "logisticImage2",
    logisticImage2Alt = "logisticImage2Alt",
    logisticText = "logisticText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.freightlink]: string;
        [fields.phoneNo]: string;
        [fields.freightContent]: string;
        [fields.phoneContent]: string;
        [fields.phoneHeading]: string;
        [fields.freightContentColor]: string;
        [fields.headingColor]: string;
        [fields.shippingRateBackgroundColor]: string;
        [fields.logisticImage1]: string;
        [fields.logisticImage1Alt]: string;
        [fields.logisticImage2]: string;
        [fields.logisticImage2Alt]: string;
        [fields.logisticText]: string;
    };
}
const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        countries: getCurrentCountries(state),
        shouldLoadCountries: !countriesDataView.value && !countriesDataView.isLoading,
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        variantProductState: getProductState(state, state.pages.productDetails.selectedProductId),
        configurationSelection: state.pages.productDetails.configurationSelection,
        configurationCompleted: state.pages.productDetails.configurationCompleted,
        subscription: state.pages.productDetails.subscription,
        isMinErrorObj: state.t_components.isMinError,
        isMultiErrorObj: state.t_components.isMultiError,
    };
};
const mapDispatchToProps = {
    displayProduct,
    loadCurrentCountries,
    freightQuoteHandler,
    cenQtyMin,
    cenQtyMulti,
    getSmartyAutocomplete, // add this
};
type Props = OwnProps &
    HasProduct &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasProductContext;

export interface CenPdpFreightStyles {
    subQTYBtn?: ButtonProps;
    addQTYBtn?: ButtonProps;
    contentContainer?: InjectableCss;
    contentStyles?: InjectableCss;
    freightContent?: InjectableCss;
    innerContainer?: InjectableCss;
    linkStyles?: InjectableCss;
    freightIconStyles?: IconPresentationProps;
    questionIconStyles?: IconPresentationProps;
    questionIconStylessmall?: IconPresentationProps;
    modalStyles?: ModalPresentationProps;
    container?: GridContainerProps;
    containerHeading?: GridContainerProps;
    LabelGridItem: GridItemProps;
    GridItemAdd1: GridItemProps;
    GridItemAdd2: GridItemProps;
    GridItemOpen: GridItemProps;
    stateGridItem: GridItemProps;
    zipcodeGridItem: GridItemProps;
    textFieldStyles?: TextFieldPresentationProps;
    textFieldStylesQty?: TextFieldPresentationProps;
    formButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    cancelButtonCustom?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    productImageWrap?: InjectableCss;
    imgStyle?: LazyImageProps;
    imgContainer?: GridItemProps;
    productName?: GridItemProps;
    stateSelect?: SelectPresentationProps;
    qtyStyle: GridItemProps;
    mainHeadingCalculate: InjectableCss;
    fieldset: InjectableCss;
    productTitle: InjectableCss;
    addAddressLineLink: InjectableCss;
    question: InjectableCss;
    billingAddressGridItem: GridItemProps;
    addrressLabel?: InjectableCss;
    errorMsgStyle?: InjectableCss;
    mainGrid?: InjectableCss;
    billingAddressGridItemAdd: GridItemProps;
    firstNameGridItemAdd2: GridItemProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    shippingRateLabel?: InjectableCss;
    shippingRate?: InjectableCss;
    add1Para?: InjectableCss;
    tooltipContainerStyles?: InjectableCss;
    tooltipContainerStylesQty?: InjectableCss;
    logisticWrapper?: InjectableCss;
    logisticImage1Style?: InjectableCss;
    logisticImage2Style?: InjectableCss;
    logisticTextStyle?: InjectableCss;
    tbdFreightMessage?: InjectableCss;
    infoIconStyles?: IconPresentationProps;
    button?: ButtonPresentationProps & InfoIconTooltipStyles;
    errorIconStyles?: IconPresentationProps;
    errorWrapper?: InjectableCss;
    addtoWrapper?: InjectableCss;
    copyButton?: ButtonPresentationProps;
    promoWrapper?: InjectableCss;
    promoLineOne?: InjectableCss;
    promoLineTwo?: InjectableCss;
    ratePromoWrapper?: InjectableCss;
    nonRatePromoWrapper?: InjectableCss;
    addressTypeToggleWrapperStyles?: InjectableCss;
    addressTypeToggleStyles?: InjectableCss;
    addressTypeToggleButtonWrapperStyles?: InjectableCss;
    addressTypeToggleButtonStyles?: InjectableCss;
    addressList?: InjectableCss;
}

export const cenPdpFreightStyles: CenPdpFreightStyles = {
    addressList: {
        css: css`
            position: absolute;
            top: 85px;
            left: 0;
            background: white;
            width: calc(100% - 30px);
            z-index: 999;
            padding: 5px 0 0;
            margin: 0 15px;
            border: 1px solid #ebebeb;
            border-radius: 24px;
            height: 200px;
            overflow: auto;
            li {
                padding: 7px 15px;
                border-bottom: 1px solid #ebebeb;
                cursor: pointer;
                &:hover {
                    background-color: #f2f2f2;
                }
            }
        `,
    },
    ratePromoWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            gap: 8px;
            width: 100%;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    nonRatePromoWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            gap: 8px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    promoWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-direction: row;
            background: #eef3f0;
            padding: 4px 11px;
            gap: 10px;
            border: 2px dashed ${getColor("primary")};
            border-radius: 4px;
            width: 100%;
        `,
    },
    promoLineOne: {
        css: css`
            font-weight: 600;
            font-size: 14px;
            line-height: 18px;
            color: ${getColor("primary")};
        `,
    },
    promoLineTwo: {
        css: css`
            font-weight: 400;
            font-size: 12px;
            line-height: 16px;
            color: ${getColor("primary")};
        `,
    },
    copyButton: {
        variant: "secondary",
        css: css`
            padding: 4px 7.5px !important;
            border-radius: 16px !important;
            max-width: 97px !important;
            min-width: 97px !important;
            min-height: 0px;
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: #fff;
            span {
                font-weight: 600;
                font-size: 12px;
                line-height: 16px;
            }
            &:disabled {
                color: ${getColor("secondary")};
                border-color: ${getColor("secondary")};
            }
        `,
    },
    addtoWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 16px;
            margin-top: 36.5px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        button[data-test-selector="tooltip_OpenTooltip"] {
                            position: absolute;
                            left: -34px;
                            top: -17px;
                        }
                    `,
                    css`
                        button[data-test-selector="tooltip_OpenTooltip"] {
                            position: absolute;
                            left: -34px;
                            top: -17px;
                        }
                    `,
                    css`
                        button[data-test-selector="tooltip_OpenTooltip"] {
                            > span {
                                left: -5px;
                            }
                        }
                    `,
                    null,
                    null,
                ])}
            @media (min-width: 1024px) and (max-width: 1198px) {
                button[data-test-selector="tooltip_OpenTooltip"] {
                    > span {
                        left: -5px;
                    }
                }
            }
        `,
    },
    errorWrapper: {
        css: css`
            display: flex;
            font-size: 14px;
            font-weight: 400;
            gap: 4px;
            margin-top: 8px;
        `,
    },
    errorIconStyles: {
        size: 20,
        css: css`
            span {
                width: 20px;
                height: 20px;
            }
        `,
    },
    addQTYBtn: {
        css: css`
            height: 16px;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 168px;
            top: 9px;
            margin-bottom: 13px;
            z-index: 1;
            border: none;
            min-height: 16px !important;
            & svg {
                color: ${COLORS.black};
                height: 16px !important;
                width: 16px !important;
                margin-left: 5px;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background: transparent;
            }
            &:active {
                background-color: transparent;
            }
            @media all and (min-width: 1707px) {
                margin-bottom: 14px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        left: 140px;
                    `,
                    css`
                        left: 140px;
                    `,
                    css`
                        left: 146px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    subQTYBtn: {
        css: css`
            height: 16px;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 62px;
            top: 9px;
            margin-bottom: 13px;
            z-index: 1;
            border: none;
            min-height: 16px !important;
            & svg {
                color: ${COLORS.black};
                height: 16px !important;
                width: 16px !important;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled {
                background-color: transparent;
            }
            &:disabled:hover {
                background-color: transparent;
            }
        `,
    },
    button: {
        orderMinAddtoCart: {
            css: css`
                font-size: 18px !important;
            `,
        },
        hiddenmdContainerStyles: {
            css: css`
                > span {
                    font-size: 18px !important;
                }
            `,
        },
        css: css`
            width: 100%;
            border-radius: 24px !important;
            width: 176px !important;
            height: 48px !important;
            white-space: nowrap;
            display: flex;
            flex-direction: column;
            justify-content: center;
            &:disabled {
                background: ${getColor("secondary")};
                border-color: ${getColor("secondary")};
                opacity: 0.5;
            }
            > span {
                font-size: ${TYPOGRAPHY.size.large};
                font-family: ${TYPOGRAPHY.fontFamily.base};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                text-align: center;
                ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null, null])}
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: max-content !important;
                            padding: 0 10px !important;
                        `,
                        css``,
                        css`
                            width: 150px !important;
                            padding: 0 10px !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,

        hiddenMobileToolTip: {
            css: css`
                display: inline;
                position: relative;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            left: 35px;
                            top: 12px;
                            position: absolute;
                        `,
                        css`
                            left: 35px;
                            top: 28px;
                            position: absolute;
                        `,
                        css`
                            left: 4%;
                            top: 12px;
                            position: absolute;
                        `,
                        css`
                            @media (min-width: 992px) and (max-width: 1024px) {
                                top: 12px;
                                left: 10%;
                                position: absolute;
                            }
                        `,
                        null,
                    ])}
            `,
        },
        mobileTooltip: {
            cssOverrides: {
                tooltipBody: css`
                    left: 0px;
                `,
            },
        },
        tooltipButtonContainer: {
            css: css`
                display: flex;
                position: relative;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                            margin-top: 0px;
                        `,
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                            margin-top: 0px;
                        `,
                        css`
                            flex-direction: column-reverse;
                            align-items: flex-start !important;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
    tooltipContainerStyles: {
        css: css`
            background-color: ${getProp("theme.typography.p.color")};
            border-radius: 4px;
            width: max-content;
            border-radius: 4px;
            box-shadow: 0px 4px 8px 0px rgba(38, 37, 37, 0.24);
            position: absolute;
            top: -40px;
            right: 35px;
            z-index: 999;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px;
            padding: 10px;
            color: white;
            @media (max-width: 1024px) {
                top: -42px;
                right: 0px;
            }
            &:after {
                content: "";
                border-left: 0px solid ${getProp("theme.typography.p.color")};
                border-right: 4px solid ${getProp("theme.typography.p.color")};
                position: absolute;
                z-index: 999;
                top: 29px;
                left: 42%;
                width: 0px;
                height: 16px;
                width: 0px;
                height: 0px;
                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
                border-top: 16px solid ${getProp("theme.typography.p.color")};
            }
        `,
    },
    tooltipContainerStylesQty: {
        css: css`
            background-color: #262525;
            border-radius: 4px;
            width: max-content;
            border-radius: 4px;
            box-shadow: 0px 4px 8px 0px rgba(38, 37, 37, 0.24);
            position: absolute;
            top: -28px;
            right: 69px;
            z-index: 999;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px;
            padding: 8px 16px;
            color: #262525;
            &:after {
                content: "";
                border-left: 0px solid ${getProp("theme.typography.p.color")};
                border-right: 4px solid ${getProp("theme.typography.p.color")};
                position: absolute;
                z-index: 999;
                top: 29px;
                left: 42%;
                width: 0px;
                height: 16px;
                width: 0px;
                height: 0px;
                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
                border-top: 16px solid ${getProp("theme.typography.p.color")};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            top: -30px;
                            right: 0px;
                            left: 4px;
                        `,
                        css`
                            top: -30px;
                            right: 0px;
                            left: 4px;
                        `,
                        css`
                            right: 60px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    firstNameGridItemAdd2: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0px 0px 0px 0px !important;
        `,
    },
    mainGrid: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },

    errorMsgStyle: {
        css: css`
            color: ${getColor("danger")};
        `,
    },
    addrressLabel: {
        css: css`
            font-weight: 600;
            font-size: 25px;
            color: ${getProp("theme.typography.p.color")} !important ;
        `,
    },
    shippingRateLabel: {
        css: css`
            font-size: 22px;
            font-weight: 600;
            color: ${getProp("theme.typography.p.color")} !important ;
            padding: 8px 16px;
            line-height: 32px;
            width: 100%;
        `,
    },
    shippingRate: {
        css: css`
            font-weight: 600;
            color: ${getProp("theme.typography.p.color")} !important ;
        `,
    },
    add1Para: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                    `,
                    css`
                        padding-left: 0px;
                    `,
                    null,
                    css``,
                    css``,
                ])}
        `,
    },
    billingAddressGridItem: {
        width: 12,
        css: css`
            flex-basis: 61%;
            max-width: 61%;
            justify-content: center;
            padding: 0px !important;
            margin-left: 30px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-wrap: wrap;
                        flex-basis: 100%;
                        max-width: 100%;
                        justify-content: flex-start;
                        margin-left: 0px !important;
                    `,
                    css`
                        flex-wrap: wrap;
                        flex-basis: 100%;
                        max-width: 100%;
                        justify-content: flex-start;
                        margin-left: 0px !important;
                    `,
                    null,
                    css``,
                    css``,
                ])}
            p {
                font-size: 20px;
                display: inline-block;
            }
        `,
    },
    billingAddressGridItemAdd: {
        width: 12,
        css: css`
            padding-bottom: 0px !important;
            justify-content: flex-start;
            padding-left: 54px !important;
            flex-basis: 85%;
            max-width: 85%;
            flex-wrap: wrap;
            margin-left: 53px !important;
            margin-top: 12px !important;
            padding-top: 0px !important;
            @media (max-width: 1200px) {
                padding-left: 50px !important;
                padding-right: 30px !important;
                margin-left: 53px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-wrap: wrap;
                        flex-basis: 100%;
                        max-width: 100%;
                        justify-content: flex-start;
                        padding-left: 15px !important;
                        margin-left: 0px !important;
                    `,
                    css`
                        flex-wrap: wrap;
                        flex-basis: 100%;
                        max-width: 100%;
                        justify-content: flex-start;
                        padding-left: 15px !important;
                        margin-left: 0px !important;
                    `,
                    css`
                        margin-left: 45px !important;
                    `,
                    css``,
                    css``,
                ])}
            p {
                font-size: 20px;
                padding-right: 10px;
            }
        `,
    },
    question: {
        css: css`
            display: flex;
            align-items: center;
            font-family: Poppins;
            font-size: 14px !important;
            font-weight: 400 !important;
            line-height: 20px !important;
            color: #262525;
            margin-top: 10px;
        `,
    },
    addAddressLineLink: {
        css: css`
            margin-left: 24px;
            padding: 0px !important;
            font-weight: 600;
            font-size: 12px;
            line-height: 16px;
            font-family: Poppins;
            justify-content: center;
            padding-left: 0px !important;
        `,
    },
    container: {
        css: css`
            label {
                font-family: Poppins;
                font-size: 14px;
                font-weight: 600;
                line-height: 16px;
                color: #262525;
                margin-left: 24px;
            }
            label[data-test-selector="product_qtyOrdered-label"] {
                margin-left: 0px !important;
                max-height: 48px !important;
                padding-right: 0px !important;
                padding-top: 15px !important;
                max-width: 40px;
            }
            input {
                padding-left: 24px !important;
                padding-right: 24px !important;
            }
        `,
    },
    containerHeading: {
        css: css`
            div[class^="GridOffset"] {
                align-items: center;
            }
            label {
                font-family: Poppins;
                font-size: 14px;
                font-weight: 600;
                line-height: 16px;
                color: #262525;
                margin-left: 24px;
            }
            label[data-test-selector="product_qtyOrdered-label"] {
                margin-left: 0px !important;
                max-height: 48px !important;
                padding-right: 0px !important;
                padding-top: 15px !important;
                max-width: 40px;
            }
            input {
                padding-left: 24px !important;
                padding-right: 24px !important;
            }
        `,
    },
    fieldset: {
        css: css`
            border: none !important;
            margin: 0px !important;
        `,
    },
    productTitle: {
        css: css`
            font-family: Poppins;
            font-size: 14px;
            font-weight: 400;
            line-height: 20px;
            color: #262525;
        `,
    },
    LabelGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0px 0px 0px 0px !important;
            margin-top: 24px !important;
        `,
    },
    GridItemOpen: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0px 0px 8px 0px !important;
            margin-top: 18px !important;
        `,
    },
    GridItemAdd1: {
        width: [12, 12, 12, 12, 12],
        css: css`
            position: relative;
            padding: 0px 0px 8px 0px !important;
            margin-top: 38px !important;
        `,
    },
    GridItemAdd2: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0px 0px 8px 0px !important;
        `,
    },
    stateGridItem: {
        width: [12, 12, 12, 8, 12],
        css: css`
            padding: 0px 0px 0px 0px !important;
            margin-top: 24px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        margin-right: 16px !important;
                    `,
                    null,
                ])}
        `,
    },
    qtyStyle: {
        width: [12, 12, 3, 3, 3],
        css: css`
            padding: 0px;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 20px !important;
                    `,
                    css`
                        margin-top: 20px !important;
                    `,
                    css`
                        flex-basis: 27% !important;
                        max-width: 27% !important;
                    `,
                    css`
                        max-width: 207px !important;
                    `,
                    css`
                        max-width: 207px !important;
                    `,
                ])}
        `,
    },
    zipcodeGridItem: {
        width: [12, 12, 12, 4, 12],
        css: css`
            padding: 0px 0px 0px 0px !important;
            margin-top: 24px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: auto;
                    `,
                    css`
                        max-width: auto;
                    `,
                    css`
                        max-width: auto;
                    `,
                    css`
                        max-width: 261px;
                    `,
                    css`
                        max-width: auto;
                    `,
                ])}
        `,
    },
    stateSelect: {
        css: css`
            background-color: #f2f2f2 !important;
            border-radius: 24px !important;
            padding-left: 24px !important;
            font-family: Poppins !important;
            font-weight: 400 !important;
            font-size: 14px !important;
            line-height: 16px !important;
            color: ${getColor("text.main")} !important;
        `,
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 2px !important;
            `,
        },
    },
    imgContainer: {
        width: [4, 4, 1, 1, 1],

        css: css`
            padding: 0px !important;
            max-height: 80px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        max-width: 60%;
                        flex-basis: 30%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    submitButton: {
        css: css`
            border-radius: 24px;
            padding: 12px 24px;
            margin-left: -12px;
            span {
                font-weight: 600 !important;
                font-size: 18px !important;
                line-height: 24px !important;
                font-family: Poppins !important;
            }
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            border-radius: 24px;
            margin-left: 16px;
            padding: 0px 21px;
            width: 176px;
            height: 48px;
            span {
                font-weight: 600 !important;
                font-size: 18px !important;
                line-height: 24px !important;
                font-family: Poppins !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: 100%;
                            width: 150px;
                        `,
                        css`
                            height: 100%;
                            width: 150px;
                        `,
                        css``,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    cancelButtonCustom: {
        css: css`
            margin-left: 30px;
            padding-left: 38px;
        `,
    },
    productName: {
        width: [8, 8, 9, 9, 9],
        css: css`
            display: flex;
            flex-direction: column;
            padding-left: 34px;
            padding-top: 14px;
            .calculatingFreight {
                font-weight: 600;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        max-width: 60%;
                        padding-left: 5px;
                        padding-top: 0px;
                    `,
                    css`
                        width: 100%;
                        max-width: 66.6667% !important;
                        flex-basis: 66.6667% !important;
                        padding-left: 5px;
                        padding-top: 0px;
                    `,
                    css`
                        flex-basis: 64% !important;
                        max-width: 64% !important;
                    `,
                    css`
                        flex-basis: 67% !important;
                        max-width: 67% !important;
                    `,
                    css`
                        flex-basis: 67% !important;
                        max-width: 67% !important;
                    `,
                ])}
        `,
    },
    imgStyle: {
        css: css`
            padding: 0px;
            img {
                height: 80px;
                width: 80px;
            }
        `,
    },
    productImageWrap: {
        css: css`
            flex-basis: 30%;
            div {
                background: ${getColor("background")};
                box-shadow:
                    0px 0px 1px rgba(38, 37, 37, 0.56),
                    0px 2px 8px rgba(38, 37, 37, 0.16);
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 20%;
                        max-width: 20%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    formButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            margin-top: 40px;
            margin-bottom: 24px;
            margin-left: 20px;
        `,
    },
    textFieldStyles: {
        border: "rounded",
        css: css`
            label {
                font-family: Poppins !important;
                font-weight: 600 !important;
                font-size: 14px !important;
                line-height: 16px !important;
                color: #262525 !important;
                padding-left: 24px !important;
            }
            div[class^="DescriptionWrapper"] {
                margin-left: 10px !important;
            }
        `,
        cssOverrides: {
            inputSelect: css`
                background-color: #f2f2f2;

                &.textFieldWhite {
                    background-color: white;
                }
                border-radius: 24px !important;
                &:focus {
                    border-radius: 24px !important;
                }
                &::placeholder {
                    font-family: Poppins;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 24px;
                }
            `,
            formInputWrapper: css`
                width: 100%;
                margin-top: 2px !important;

                @media (max-width: 1200px) {
                    max-width: none;
                    width: auto;
                    height: auto;
                }
            `,
        },
    },
    textFieldStylesQty: {
        border: "rounded",
        css: css`
            label {
                font-family: Poppins !important;
                font-weight: 600 !important;
                font-size: 14px !important;
                line-height: 16px !important;
                color: #262525 !important;
                padding-left: 24px !important;
            }
            div[class^="DescriptionWrapper"] {
                margin-left: 10px !important;
            }
        `,
        cssOverrides: {
            inputSelect: css`
                background-color: #f2f2f2;

                &.textFieldWhite {
                    background-color: white;
                }
                border-radius: 24px;
                &:focus {
                    border-radius: 24px !important;
                }
                &::placeholder {
                    font-family: Poppins;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 24px;
                }
            `,
            formInputWrapper: css`
                width: 100%;
                margin-top: 2px !important;

                @media (max-width: 1200px) {
                    max-width: none;
                    width: auto;
                    height: auto;
                }
                > input {
                    border-radius: 24px !important;
                    margin-left: 4px !important;
                    text-align: center;
                    &::-webkit-outer-spin-button,
                    &::-webkit-inner-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                    }
                }
                div[class^="DescriptionWrapper"] {
                    min-width: 171px !important;
                    margin-left: -38px;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            > input {
                                max-width: 132px !important;
                            }
                        `,
                        css`
                            > input {
                                max-width: 132px !important;
                            }
                        `,
                        css`
                            div[class^="DescriptionWrapper"] {
                                margin-left: -42px;
                            }
                        `,
                        css`
                            > input {
                                width: 164px !important;
                            }
                        `,
                        css`
                            > input {
                                width: 164px !important;
                            }
                        `,
                    ])}
            `,
        },
    },
    modalStyles: {
        size: 640,
        cssOverrides: {
            modalTitle: css`
                border-bottom: none;
                padding: 40px 40px 24px 40px !important;
                h4 {
                    font-family: Poppins;
                    font-weight: 700;
                    font-size: 32px;
                    line-height: 40px;
                }
                button[data-test-selector^="modalCloseButton"] {
                    position: absolute;
                    right: 16px;
                    top: 16px;

                    span {
                        color: #262525 !important;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 30px 30px 24px 30px !important;
                        `,
                        css`
                            padding: 30px 30px 24px 30px !important;
                        `,
                        null,
                        null,
                        null,
                    ])}
            `,
            modalContainer: css`
                .modalText {
                    display: inline-block;
                    padding-bottom: 40px;
                    font-weight: 400;
                    font-size: 14px;
                    line-height: 24px;
                }
                > div[aria-labelledby="modalTitle"] {
                    max-width: 936px;
                    max-height: 502px;
                }
            `,

            modalContent: css`
                padding: 0px 40px 40px 40px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 30px 30px 30px !important;
                        `,
                        css`
                            padding: 0px 30px 30px 30px !important;
                        `,
                        null,
                        null,
                        null,
                    ])}
            `,
        },
    },
    questionIconStyles: {
        size: 30,
    },
    questionIconStylessmall: {
        size: 28,
        css: css`
            padding: 5px;
        `,
    },
    freightIconStyles: {
        css: css`
            width: 22.5px !important;
            height: 30px !important;
            svg {
                width: 22.5px !important;
                height: 30px !important;
            }
        `,
    },
    linkStyles: {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 14px;
            line-height: 16px;
        `,
    },
    contentContainer: {
        css: css`
            background-color: ${getColor("primary.contrast")};
            border: 2px solid ${getColor("text.accent")};
            padding: 13px 0px 13px 16.75px;
            display: flex;
            gap: 29px;
            margin-bottom: 0px;
            margin-top: 0px;
            flex-direction: column;
            border-radius: 5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    innerContainer: {
        css: css`
            display: flex;
            align-items: center;
            gap: 12px;
        `,
    },
    contentStyles: {
        css: css`
            font-family: Poppins;
            font-weight: 400;
            font-size: 12px;
            line-height: 16px;
            color: ${getColor("text.main")};
        `,
    },
    freightContent: {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 12px;
            line-height: 16px;
            span {
                color: ${getColor("text.link")} !important;
            }
        `,
    },
    mainHeadingCalculate: {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 16px;
            line-height: 24px;
            color: #000000;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    logisticWrapper: {
        css: css`
            display: flex;
            margin-top: 6px;
            align-items: center;
        `,
    },
    logisticImage1Style: {
        css: css`
            width: 43px;
            height: 20px;
        `,
    },
    logisticImage2Style: {
        css: css`
            width: 17px;
            height: 20px;
            margin-left: 4px;
            margin-right: 8px;
        `,
    },
    logisticTextStyle: {
        css: css`
            font-size: 12px;
            line-height: 16px;
        `,
    },
    tbdFreightMessage: {
        css: css`
            font-size: 14px;
            line-height: 20px;
            color: ${getColor("text.link")};
            display: flex;
            align-items: start;
            gap: 5px;
            margin: 2px 0 0;
        `,
    },
    infoIconStyles: {
        size: 15,
        css: css`
            margin-top: 3px;
            svg {
                > path {
                    fill: ${getColor("text.link")};
                }
            }
        `,
    },
    addressTypeToggleWrapperStyles: {
        css: css`
            margin-top: 40px;
            width: 400px;
            @media all and (max-width: 480px) {
                width: 100%;
            }
        `,
    },
    addressTypeToggleStyles: {
        css: css`
            font-family: Poppins;
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            padding-left: 24px;
            color: #262525;
        `,
    },
    addressTypeToggleButtonWrapperStyles: {
        css: css`
            margin-top: 8px;
            display: flex;
            border-radius: 24px;
            overflow: hidden;
            border: 1px solid #266b40;
        `,
    },
    addressTypeToggleButtonStyles: {
        css: css`
            padding: 16px 24px;
            border: none;
            cursor: pointer;
            position: relative;
            display: flex;
            align-items: center;
            flex: 0 0 50%;
            justify-content: center;
            border-radius: 0;
            min-height: 48px;
            font-size: 14px;
            line-height: 16px;
            font-weight: 600;
            & div {
                position: relative;
            }
            & svg {
                position: absolute;
                top: -6px;
                left: -22px;
            }
        `,
    },
};

const styles = cenPdpFreightStyles;
const StyledForm = getStyledWrapper("form");
const StyledFieldSet = getStyledWrapper("fieldset");
const emailRegexp = new RegExp("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.(?!usa$)[a-zA-Z]{2,}$", "i");
const CenPdpFreightCalculatorContactBlock: FC<Props> = ({
    fields,
    id,
    loadCurrentCountries,
    shouldLoadCountries,
    countries,
    freightQuoteHandler,
    productContext: {
        product: {
            id: productId,
            smallImagePath,
            imageAltText,
            productNumber,
            productTitle,
            detail,
            brand,
            properties,
        },

        onQtyOrderedChanged,

        productInfo,
    },
    variantSelectionCompleted,
    variantProductState,
    configurationSelection,
    configurationCompleted,
    subscription,
    cenQtyMin,
    cenQtyMulti,
    isMinErrorObj,
    isMultiErrorObj,
    getSmartyAutocomplete,
}) => {
    const zipCodeRequiredFieldMessage = siteMessage("inquiryLineCard_zipCodeRequiredFieldMessage");

    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");

    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAddress2Opened, setIsAddress2Opened] = useState(false);
    const [address1, setAddress1] = useState("");
    const [address1Error, setAddress1Error] = useState("");
    const [address2, setAddress2] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [zipCodeError, setZipCodeError] = useState("");
    const [address2Error, setAddress2Error] = useState("");
    const [city, setCity] = useState("");
    const [cityError, setCityError] = useState("");
    const [stateId, setStateId] = useState("");
    const [stateError, setStateError] = useState("");
    const [states, setStates] = useState<StateModel[]>([]);
    const [state, setState] = useState<any>();
    const [formErrors, setFormErrors] = useState<any>("");
    const [email, setEmail] = useState("");
    const [resultError, setResultError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [productQty, setProductQty] = useState(productInfo.qtyOrdered.toString());
    const [onSuccess, setOnSuccess] = useState(true);
    const [freightRate, setFreightRate] = useState("");
    const [tbdfreightMessage, setTbdfreightMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [scrollTrigger, setScrollTrigger] = useState(0);
    const [showTooltip, setShowTooltip] = useState(false);
    const [showTooltipQty, setShowTooltipQty] = useState(false);
    const [buttonText, setButtonText] = useState(translate("Copy Code"));
    const [copied, setCopied] = useState(false);
    const isMinError = isMinErrorObj?.isMinError;
    const isMultiError = isMultiErrorObj?.isMultiError;
    const errorRef = useRef<HTMLDivElement | null>(null);
    const [ResidentialAddressIndicator, setResidentialAddressIndicator] = useState(false); // false = Commercial, true = Residential
    const [address1Autocomplete, setAddress1Autocomplete] = useState<any[]>([]);

    const debouncedAddress1Autocomplete = useRef(
        debounce((searchValue: string) => {
            getSmartyAutocomplete({
                search: searchValue.trim(),
                onSuccess: (result: any) => {
                    setAddress1Autocomplete(result);
                },
            });
        }, 500),
    ).current;

    useEffect(() => {
        if (resultError !== "" && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [scrollTrigger]);
    useEffect(() => {
        if (detail && detail?.shippingWeight > 150) {
            const tbdMessage = siteMessage("Shipping_Item_OverWeight");
            setTbdfreightMessage(typeof tbdMessage === "string" ? tbdMessage : "");
        }
    }, []);
    const handleMouseEnter = () => {
        if (!variantSelectionCompleted && variantProductState.value?.isVariantParent) {
            setShowTooltip(true);
        } else {
            setShowTooltip(false);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    const openModalHandler = () => {
        if (!countries) {
            loadCurrentCountries(); // remove countries call on initial load
        }
        if (!variantSelectionCompleted && variantProductState.value?.isVariantParent) {
            setIsModalOpen(false);
        } else {
            setIsModalOpen(true);
        }
    };

    useEffect(() => {
        if (productInfo.qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${productInfo.qtyOrdered || ""}`);
    }, [productInfo.qtyOrdered]);

    const selectedCountry = countries?.find(c => c.abbreviation === "US");
    useEffect(() => {
        setStates(selectedCountry?.states || []);
    }, [selectedCountry]);
    const handleStateChange: SelectProps["onChange"] = event => {
        const selectedState = selectedCountry?.states?.find(s => s.id === event.target.value);
        setState(selectedState);
        validateState(event.target.value);
    };
    const validateState = (stateId: any) => {
        const stateValidFn = isNonEmptyString(stateId);
        setStateError(!stateValidFn ? translate("State is required.") : "");
        return stateValidFn;
    };
    const validateEmail = (email: string) => {
        const errorMessage = !email ? "" : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(typeof errorMessage === "string" ? errorMessage : "");
        return !errorMessage;
    };
    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };
    const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.value.includes("@")) {
            setAddress1(event.currentTarget.value);
            validateAddress1(event.currentTarget.value);
        }
    };
    const validateAddress1 = (address1: string) => {
        const address1Valid = isNonEmptyString(address1);
        setAddress1Error(!address1Valid ? translate("Address is required.") : "");

        if (address1Valid && address1.trim().length >= 3) {
            debouncedAddress1Autocomplete(address1);
        } else {
            setAddress1Autocomplete([]);
        }

        return address1Valid;
    };
    const updateAddress = (address: any) => {
        const selectedState = selectedCountry?.states?.find(s => s.id === address.stateId);

        setState(selectedState);
        setAddress1Autocomplete([]); // clear dropdown
        setAddress1(address.street_line);
        setAddress1Error("");
        setZipCode(address.zipcode);
        setZipCodeError("");
        setCity(address.city);
        setCityError("");

        if (address.secondary) {
            setIsAddress2Opened(true);
            setAddress2(address.secondary);
            setAddress2Error("");
        } else {
            setIsAddress2Opened(false);
            setAddress2("");
        }
    };
    const handleAddress2Change = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.value.includes("@")) {
            setAddress2(event.currentTarget.value);
            validateAddress2(event.currentTarget.value);
        }
    };
    const validateAddress2 = (address2: string) => {
        const address2Valid = isNonEmptyString(address2);
        setAddress2Error(!address2Valid ? translate("Address is required.") : "");
        return address2Valid;
    };
    const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCity(event.currentTarget.value);
        validateCity(event.currentTarget.value);
    };
    const validateCity = (city: string) => {
        const cityValid = isNonEmptyString(city);
        setCityError(!cityValid ? translate("City is required.") : "");
        return cityValid;
    };
    const zipCodeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setZipCode(e.target.value);
        validateZipCode(e.target.value);
    };

    const validateZipCode = (zipCode: string) => {
        const errorMessage = isNonEmptyString(String(zipCode));
        setZipCodeError(!errorMessage ? String(zipCodeRequiredFieldMessage) : "");
        return errorMessage;
    };
    const validateForm = () => {
        if (isMinError || isMultiError) {
            return false;
        }
        const address1Valid = validateAddress1(address1);
        const stateValid = validateState(state?.id);
        const cityValid = validateCity(city);
        const zipCodeValid = validateZipCode(zipCode);
        const emailValid = validateEmail(email);
        return address1Valid && cityValid && zipCodeValid && emailValid && stateValid;
    };
    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };
    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (detail?.multipleSaleQty && detail?.multipleSaleQty > 0 && detail?.roundingRule === "Multiple Only") {
            if (Number(value) < detail?.multipleSaleQty) {
                cenQtyMin({ isMinError: true });
                cenQtyMulti({ isMultiError: false });
            } else if (Number(value) > detail?.multipleSaleQty && Number(value) % detail?.multipleSaleQty !== 0) {
                cenQtyMin({ isMinError: false });
                cenQtyMulti({ isMultiError: true });
            } else {
                cenQtyMin({ isMinError: false });
                cenQtyMulti({ isMultiError: false });
            }
        }
        setProductQty(value);
        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }
        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };
    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validateForm()) {
            setFormErrors(true);
            return;
        }
        setIsLoading(true);
        freightQuoteHandler({
            emailId: email,
            destAddrLine1: address1,
            destAddrLine2: address2,
            destAddrCity: city,
            destAddrState: state.abbreviation,
            destAddrZip: zipCode,
            productNumber,
            stockQuantityInHand: Number(productInfo.inventory?.qtyOnHand ? productInfo.inventory?.qtyOnHand : ""),
            unitNetPrice: productInfo?.pricing?.unitNetPrice ?? 0,
            qtyOrdered: Number(productQty),
            ResidentialAddressIndicator,
            onSuccess: result => {
                setFreightRate(result.freightRate);
                setIsLoading(false);
                setOnSuccess(false);
            },
            onError: error => {
                setIsLoading(false);
                setResultError(typeof siteMessage(error) === "string" ? (siteMessage(error) as string) : "");
                setScrollTrigger(prev => prev + 1);
            },
        });
    };

    const freightIconStyles = {
        css: css`
            width: 22.5px !important;
            height: 30px !important;
            svg {
                width: 22.5px !important;
                height: 30px !important;
                > path {
                    fill: ${fields.freightContentColor};
                }
            }
        `,
    };

    const questionIconStyles = {
        size: 30,
        css: css`
            svg {
                width: 22.5px !important;
                height: 30px !important;
                > path {
                    fill: ${fields.freightContentColor};
                }
            }
        `,
    };

    const linkStyles = {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 14px;
            line-height: 16px;
            > span {
                color: ${fields.headingColor} !important;
            }
        `,
    };
    const questionIconStylessmall = {
        size: 28,
        css: css`
            padding: 5px;
            svg {
                > path {
                    fill: ${fields.freightContentColor};
                }
        `,
    };

    const modalCloseHandler = () => {
        setIsModalOpen(false);
        setAddress1("");
        setAddress2("");
        setCity("");
        setState("");
        setZipCode("");
        setEmail("");
        setOnSuccess(true);
        setAddress1Error("");
        setCityError("");
        setAddress2("");
        setStateError("");
        setZipCodeError("");
        setEmailError("");
        setCityError("");
        setResultError("");
        setProductQty(productInfo.qtyOrdered.toString());
        setButtonText("Copy Code");
        setCopied(false);
        setAddress1Autocomplete([]);
    };
    const handleMouseEnterQty = () => {
        if (
            detail?.multipleSaleQty &&
            detail?.multipleSaleQty > 0 &&
            detail?.roundingRule === "Multiple Only" &&
            Number(productQty) === detail?.multipleSaleQty
        ) {
            setShowTooltipQty(true);
        } else {
            setShowTooltipQty(false);
        }
    };

    const handleMouseLeaveQty = () => {
        setShowTooltipQty(false);
    };
    const addQtyFn = () => {
        const isMultipleOnly =
            detail?.multipleSaleQty && detail?.multipleSaleQty > 0 && detail?.roundingRule === "Multiple Only";

        const newQty = Number(productQty) + (isMultipleOnly ? detail.multipleSaleQty : 1);

        if (isMultipleOnly && newQty === detail.multipleSaleQty) {
            cenQtyMin({ isMinError: false });
            cenQtyMulti({ isMultiError: false });
        }

        setProductQty(newQty.toString());

        if (onQtyOrderedChanged) {
            onQtyOrderedChanged(newQty);
        }
    };

    const subQtyFn = () => {
        const decrement =
            detail?.multipleSaleQty && detail?.multipleSaleQty > 0 && detail?.roundingRule === "Multiple Only"
                ? detail?.multipleSaleQty
                : 1;
        const qtyValue = Number(productQty) - decrement;
        if (qtyValue <= 0) {
            setProductQty("0");
            if (onQtyOrderedChanged) {
                onQtyOrderedChanged(0);
            }
            return;
        }
        setProductQty(qtyValue.toString());
        if (onQtyOrderedChanged) {
            onQtyOrderedChanged(qtyValue);
        }
    };
    const promoCode = properties?.promotionCode;
    const promoVerbiage = properties?.description;
    const copyCode = () => {
        navigator.clipboard.writeText(promoCode ?? "").then(() => {
            setButtonText(translate("Copied!"));
            setCopied(true);
        });
    };
    const promoSytles = promoCode ? { ...styles.ratePromoWrapper } : { ...styles.nonRatePromoWrapper };

    return (
        <>
            <StyledWrapper {...styles.contentContainer}>
                <StyledWrapper {...styles.innerContainer}>
                    <Icon src={CenFreightIcon} {...freightIconStyles} />
                    <StyledWrapper style={{ position: "relative" }}>
                        <Link
                            href=""
                            onClick={openModalHandler}
                            {...linkStyles}
                            onMouseOver={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            disabled={!variantSelectionCompleted && variantProductState.value?.isVariantParent}
                        >
                            {fields.freightlink}
                        </Link>
                        {showTooltip && (
                            <StyledWrapper {...styles.tooltipContainerStyles}>
                                <Typography
                                    as="span"
                                    css={css`
                                        color: ${getColor("info.contrast")} !important;
                                    `}
                                >
                                    {siteMessage("Variant_PDP_Freight")}
                                </Typography>
                            </StyledWrapper>
                        )}
                        <Modal
                            isOpen={isModalOpen}
                            handleClose={modalCloseHandler}
                            headline={translate("Freight Calculator")}
                            {...styles.modalStyles}
                            as="h3"
                        >
                            <StyledForm
                                id="freightForm"
                                noValidate={true}
                                onSubmit={handleFormSubmit}
                                data-test-selector="freightForm"
                            >
                                <StyledFieldSet {...styles.fieldset}>
                                    <GridContainer {...styles.containerHeading} data-test-selector="addTagsFormHeading">
                                        <GridItem {...styles.imgContainer}>
                                            <StyledWrapper {...styles.productImageWrap}>
                                                <LazyImage
                                                    {...styles.imgStyle}
                                                    src={smallImagePath}
                                                    altText={imageAltText}
                                                />
                                            </StyledWrapper>
                                        </GridItem>
                                        <GridItem {...styles.productName}>
                                            <Typography
                                                as="p"
                                                className="calculatingFreight"
                                                {...styles.mainHeadingCalculate}
                                            >
                                                {onSuccess
                                                    ? translate("Calculating freight for:")
                                                    : translate("Calculated freight for:")}
                                            </Typography>
                                            <Typography as="p" {...styles.productTitle}>
                                                {productTitle}
                                            </Typography>
                                            {tbdfreightMessage && (
                                                <div>
                                                    <Typography as="p" {...styles.tbdFreightMessage}>
                                                        <Icon src={CustomInfoIcon} {...styles.infoIconStyles}></Icon>
                                                        {tbdfreightMessage}
                                                    </Typography>
                                                </div>
                                            )}
                                        </GridItem>
                                        <GridItem {...styles.qtyStyle}>
                                            <Button
                                                type="button"
                                                disabled={
                                                    !!(
                                                        (detail?.multipleSaleQty &&
                                                            detail?.multipleSaleQty > 0 &&
                                                            detail?.roundingRule === "Multiple Only" &&
                                                            Number(productQty) === detail?.multipleSaleQty) ||
                                                        !onSuccess ||
                                                        isLoading
                                                    )
                                                }
                                                {...styles.subQTYBtn}
                                                data-test-selector="subQTYBtn"
                                                onClick={subQtyFn}
                                                onMouseOver={handleMouseEnterQty}
                                                onMouseLeave={handleMouseLeaveQty}
                                            >
                                                <ButtonIcon src={Minus} />
                                            </Button>
                                            {showTooltipQty && (
                                                <StyledWrapper {...styles.tooltipContainerStylesQty}>
                                                    <Typography
                                                        as="span"
                                                        css={css`
                                                            color: ${getColor("info.contrast")} !important;
                                                        `}
                                                    >
                                                        {translate(`${detail?.multipleSaleQty} unit minimum`)}
                                                    </Typography>
                                                </StyledWrapper>
                                            )}
                                            <TextField
                                                label={translate("QTY")}
                                                labelPosition="left"
                                                value={productQty}
                                                type="number"
                                                min={0}
                                                max={999999}
                                                error={
                                                    isMinError ? (
                                                        <StyledWrapper {...styles.errorWrapper}>
                                                            <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                                                            <Typography>
                                                                {translate(
                                                                    `This product has a minimum order of ${detail?.multipleSaleQty} units`,
                                                                )}
                                                            </Typography>
                                                        </StyledWrapper>
                                                    ) : isMultiError ? (
                                                        <StyledWrapper {...styles.errorWrapper}>
                                                            <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                                                            <Typography>
                                                                {translate(
                                                                    `This product must be ordered in multiples of ${detail?.multipleSaleQty}`,
                                                                )}
                                                            </Typography>
                                                        </StyledWrapper>
                                                    ) : undefined
                                                }
                                                disabled={!onSuccess || isLoading}
                                                required
                                                data-test-selector="product_qtyOrdered"
                                                onKeyPress={keyPressHandler}
                                                onChange={qtyChangeHandler}
                                                {...styles.textFieldStylesQty}
                                            />
                                            <Button
                                                {...styles.addQTYBtn}
                                                type="button"
                                                data-test-selector="addQTYBtn"
                                                onClick={addQtyFn}
                                                disabled={!onSuccess || isLoading}
                                            >
                                                <ButtonIcon src={Plus} />
                                            </Button>
                                        </GridItem>
                                    </GridContainer>
                                    <GridContainer {...styles.container} data-test-selector="addTagsForm">
                                        {onSuccess ? (
                                            isLoading ? (
                                                <StyledWrapper {...styles.centeringWrapper}>
                                                    <LoadingSpinner {...styles.spinner} />
                                                </StyledWrapper>
                                            ) : (
                                                <>
                                                    <GridItem {...styles.LabelGridItem}>
                                                        <StyledWrapper {...styles.addressTypeToggleWrapperStyles}>
                                                            <StyledWrapper {...styles.addressTypeToggleStyles}>
                                                                {translate("Address Type*")}
                                                            </StyledWrapper>
                                                            <StyledWrapper
                                                                {...styles.addressTypeToggleButtonWrapperStyles}
                                                            >
                                                                <Button
                                                                    {...styles.addressTypeToggleButtonStyles}
                                                                    onClick={e => {
                                                                        e.preventDefault();
                                                                        setResidentialAddressIndicator(false); // Commercial
                                                                    }}
                                                                    style={{
                                                                        backgroundColor: !ResidentialAddressIndicator
                                                                            ? "#2d6a4f"
                                                                            : "#ffffff",
                                                                        color: !ResidentialAddressIndicator
                                                                            ? "#ffffff"
                                                                            : "#666666",
                                                                    }}
                                                                >
                                                                    {!ResidentialAddressIndicator && (
                                                                        <div>
                                                                            <svg
                                                                                width="16"
                                                                                height="12"
                                                                                viewBox="0 0 16 12"
                                                                                fill="none"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                            >
                                                                                <path
                                                                                    d="M15.875 1.5C15.875 1.78786 15.7651 2.07586 15.5454 2.29523L6.54541 11.2952C6.32656 11.516 6.03828 11.625 5.75 11.625C5.46172 11.625 5.17414 11.5151 4.95477 11.2954L0.454766 6.79541C0.235039 6.57656 0.125 6.28828 0.125 6C0.125 5.35734 0.650586 4.875 1.25 4.875C1.53786 4.875 1.82586 4.98486 2.04523 5.20459L5.75 8.91094L13.9555 0.705469C14.1734 0.485039 14.4617 0.375 14.75 0.375C15.3512 0.375 15.875 0.856641 15.875 1.5Z"
                                                                                    fill="white"
                                                                                />
                                                                            </svg>
                                                                        </div>
                                                                    )}
                                                                    {translate("Commercial")}
                                                                </Button>
                                                                <Button
                                                                    {...styles.addressTypeToggleButtonStyles}
                                                                    onClick={e => {
                                                                        e.preventDefault();
                                                                        setResidentialAddressIndicator(true); // Residential
                                                                    }}
                                                                    style={{
                                                                        backgroundColor: ResidentialAddressIndicator
                                                                            ? "#2d6a4f"
                                                                            : "#ffffff",
                                                                        color: ResidentialAddressIndicator
                                                                            ? "#ffffff"
                                                                            : "#666666",
                                                                    }}
                                                                >
                                                                    {ResidentialAddressIndicator && (
                                                                        <div>
                                                                            <svg
                                                                                width="16"
                                                                                height="12"
                                                                                viewBox="0 0 16 12"
                                                                                fill="none"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                            >
                                                                                <path
                                                                                    d="M15.875 1.5C15.875 1.78786 15.7651 2.07586 15.5454 2.29523L6.54541 11.2952C6.32656 11.516 6.03828 11.625 5.75 11.625C5.46172 11.625 5.17414 11.5151 4.95477 11.2954L0.454766 6.79541C0.235039 6.57656 0.125 6.28828 0.125 6C0.125 5.35734 0.650586 4.875 1.25 4.875C1.53786 4.875 1.82586 4.98486 2.04523 5.20459L5.75 8.91094L13.9555 0.705469C14.1734 0.485039 14.4617 0.375 14.75 0.375C15.3512 0.375 15.875 0.856641 15.875 1.5Z"
                                                                                    fill="white"
                                                                                />
                                                                            </svg>
                                                                        </div>
                                                                    )}
                                                                    {translate("Residential")}
                                                                </Button>
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    </GridItem>
                                                    {/* FORM FIELDS */}
                                                    <GridItem {...styles.GridItemAdd1}>
                                                        <TextField
                                                            {...styles.textFieldStyles}
                                                            name="Address 1"
                                                            label={translate("Address 1")}
                                                            value={address1}
                                                            required
                                                            maxLength={100}
                                                            onChange={handleAddressChange}
                                                            error={address1Error}
                                                            data-test-selector="customerAddressForm_firstName"
                                                        />

                                                        {address1Autocomplete.length > 0 && (
                                                            <StyledWrapper as="ul" {...styles.addressList}>
                                                                {address1Autocomplete.map(address => (
                                                                    <StyledWrapper
                                                                        as="li"
                                                                        key={address.street_line}
                                                                        onClick={() => updateAddress(address)}
                                                                    >
                                                                        {`${address.street_line}, ${address.city}`}
                                                                    </StyledWrapper>
                                                                ))}
                                                            </StyledWrapper>
                                                        )}
                                                    </GridItem>

                                                    {!isAddress2Opened ? (
                                                        <GridItem {...styles.GridItemAdd2}>
                                                            <Link
                                                                {...styles.addAddressLineLink}
                                                                onClick={() => setIsAddress2Opened(true)}
                                                                data-test-selector="customerAddressForm_addressLine2"
                                                            >
                                                                {translate("+ Address line 2")}
                                                            </Link>
                                                        </GridItem>
                                                    ) : (
                                                        <>
                                                            <GridItem {...styles.GridItemOpen}>
                                                                <TextField
                                                                    {...styles.textFieldStyles}
                                                                    name="address2"
                                                                    label="Address2"
                                                                    value={address2}
                                                                    maxLength={100}
                                                                    onChange={handleAddress2Change}
                                                                    error={address2Error}
                                                                    data-test-selector="customerAddressForm_address2"
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.firstNameGridItemAdd2}>
                                                                <Link
                                                                    {...styles.addAddressLineLink}
                                                                    onClick={() => setIsAddress2Opened(false)}
                                                                    data-test-selector="customerAddressForm_addressLine2"
                                                                >
                                                                    {translate("- Address line 2")}
                                                                </Link>
                                                            </GridItem>
                                                        </>
                                                    )}

                                                    <GridItem {...styles.LabelGridItem}>
                                                        <TextField
                                                            {...styles.textFieldStyles}
                                                            name="City"
                                                            label={translate("City")}
                                                            value={city}
                                                            required
                                                            onChange={handleCityChange}
                                                            error={cityError}
                                                            data-test-selector="customerAddressForm_firstName"
                                                        />
                                                    </GridItem>

                                                    {states.length > 0 && (
                                                        <GridItem {...styles.stateGridItem}>
                                                            <Select
                                                                {...styles.stateSelect}
                                                                label="State"
                                                                value={state?.id || ""}
                                                                required
                                                                onChange={handleStateChange}
                                                                error={stateError}
                                                                data-test-selector="customerAddressForm_state"
                                                            >
                                                                <option value="">{translate("Select State")}</option>
                                                                {states.map(st => (
                                                                    <option key={st.id} value={st.id}>
                                                                        {st.name}
                                                                    </option>
                                                                ))}
                                                            </Select>
                                                        </GridItem>
                                                    )}

                                                    <GridItem {...styles.zipcodeGridItem}>
                                                        <TextField
                                                            {...styles.textFieldStyles}
                                                            name="Zip Code"
                                                            label={translate("Zip Code")}
                                                            value={zipCode}
                                                            required
                                                            maxLength={5}
                                                            onChange={zipCodeHandler}
                                                            error={zipCodeError}
                                                            data-test-selector="customerAddressForm_firstName"
                                                        />
                                                    </GridItem>

                                                    <GridItem {...styles.LabelGridItem}>
                                                        <TextField
                                                            {...styles.textFieldStyles}
                                                            name="email"
                                                            label={translate("Email")}
                                                            value={email}
                                                            // required
                                                            onChange={event => emailChangeHandler(event.target.value)}
                                                            error={emailError}
                                                            data-test-selector="customerAddressForm_firstName"
                                                        />
                                                    </GridItem>

                                                    {resultError !== "" && (
                                                        <StyledWrapper {...styles.LabelGridItem} ref={errorRef}>
                                                            <Typography {...styles.errorMsgStyle}>
                                                                {siteMessage(resultError)}
                                                            </Typography>
                                                        </StyledWrapper>
                                                    )}

                                                    <StyledWrapper
                                                        {...styles.logisticWrapper}
                                                        style={{ marginTop: "24px", width: "100%" }}
                                                    >
                                                        {fields.logisticImage1 && (
                                                            <Img
                                                                src={fields.logisticImage1}
                                                                {...styles.logisticImage1Style}
                                                                altText={fields.logisticImage1Alt}
                                                                style={{ width: "51px", height: "24px" }}
                                                            />
                                                        )}
                                                        {fields.logisticImage2 && (
                                                            <Img
                                                                src={fields.logisticImage2}
                                                                {...styles.logisticImage2Style}
                                                                altText={fields.logisticImage2Alt}
                                                                style={{ width: "20px", height: "24px" }}
                                                            />
                                                        )}
                                                        {fields.logisticText && (
                                                            <StyledWrapper {...styles.logisticTextStyle}>
                                                                {fields.logisticText}
                                                            </StyledWrapper>
                                                        )}
                                                    </StyledWrapper>

                                                    <StyledWrapper {...styles.formButtonsWrapper}>
                                                        <Button
                                                            {...styles.submitButton}
                                                            type="submit"
                                                            data-test-selector="customerAddressForm_save"
                                                        >
                                                            {translate("Calculate My Freight")}
                                                        </Button>
                                                        <Button
                                                            {...styles.cancelButton}
                                                            type="button"
                                                            data-test-selector="customerAddressForm_cancel"
                                                            onClick={modalCloseHandler}
                                                        >
                                                            {translate("Close Window")}
                                                        </Button>
                                                    </StyledWrapper>
                                                </>
                                            )
                                        ) : (
                                            <>
                                                <GridItem {...styles.billingAddressGridItemAdd}>
                                                    <Typography as="p" {...styles.addrressLabel}>
                                                        {translate("Address:")}
                                                    </Typography>
                                                    <Typography as="p" {...styles.add1Para}>
                                                        {address1},
                                                    </Typography>
                                                    {address2 && <Typography as="p">{address2},</Typography>}
                                                    <Typography as="p">{city},</Typography>
                                                    <Typography as="p">{state?.name},</Typography>
                                                    <Typography as="p">{zipCode}</Typography>
                                                </GridItem>

                                                {freightRate && (
                                                    <GridItem
                                                        {...styles.billingAddressGridItem}
                                                        css={css`
                                                            display: flex;
                                                            flex-direction: column;
                                                            padding: 15px 0px 0px 0px !important;
                                                        `}
                                                    >
                                                        <StyledWrapper {...promoSytles}>
                                                            <StyledWrapper
                                                                {...styles.shippingRateLabel}
                                                                style={{
                                                                    background: `${fields.shippingRateBackgroundColor}`,
                                                                }}
                                                            >
                                                                {translate("Shipping Cost for this Item:")}{" "}
                                                                <Typography
                                                                    as="span"
                                                                    {...styles.shippingRate}
                                                                    style={{
                                                                        background: `${fields.shippingRateBackgroundColor}`,
                                                                    }}
                                                                >
                                                                    {freightRate}
                                                                </Typography>
                                                            </StyledWrapper>
                                                            {promoCode && (
                                                                <StyledWrapper {...styles.promoWrapper}>
                                                                    <StyledWrapper
                                                                        style={{
                                                                            display: "flex",
                                                                            flexDirection: "column",
                                                                        }}
                                                                    >
                                                                        <Typography {...styles.promoLineOne}>
                                                                            {translate("Use Promo Code")}{" "}
                                                                            <Typography
                                                                                as="span"
                                                                                style={{ fontWeight: 700 }}
                                                                            >
                                                                                {promoCode}
                                                                            </Typography>
                                                                        </Typography>
                                                                        {promoVerbiage && (
                                                                            <Typography {...styles.promoLineTwo}>
                                                                                {promoVerbiage}
                                                                            </Typography>
                                                                        )}
                                                                    </StyledWrapper>
                                                                    <Button
                                                                        disabled={copied}
                                                                        onClick={copyCode}
                                                                        {...styles.copyButton}
                                                                    >
                                                                        {buttonText}
                                                                    </Button>
                                                                </StyledWrapper>
                                                            )}
                                                        </StyledWrapper>
                                                        <StyledWrapper {...styles.logisticWrapper}>
                                                            {fields.logisticImage1 && (
                                                                <Img
                                                                    src={fields.logisticImage1}
                                                                    {...styles.logisticImage1Style}
                                                                    altText={fields.logisticImage1Alt}
                                                                    style={{ width: "51px", height: "24px" }}
                                                                />
                                                            )}
                                                            {fields.logisticImage2 && (
                                                                <Img
                                                                    src={fields.logisticImage2}
                                                                    {...styles.logisticImage2Style}
                                                                    altText={fields.logisticImage2Alt}
                                                                    style={{ width: "20px", height: "24px" }}
                                                                />
                                                            )}
                                                            {fields.logisticText && (
                                                                <StyledWrapper {...styles.logisticTextStyle}>
                                                                    {fields.logisticText}
                                                                </StyledWrapper>
                                                            )}
                                                        </StyledWrapper>
                                                    </GridItem>
                                                )}
                                            </>
                                        )}
                                    </GridContainer>
                                </StyledFieldSet>
                            </StyledForm>
                            {!onSuccess && (
                                <StyledWrapper {...styles.addtoWrapper}>
                                    <ProductAddToCartButton
                                        configurationSelection={configurationSelection}
                                        configurationCompleted={configurationCompleted}
                                        variantSelectionCompleted={variantSelectionCompleted}
                                        subscription={subscription}
                                        extendedStyles={styles.button}
                                        data-test-selector="addProductToCart"
                                    />
                                    <GridItem style={{ maxWidth: "100%" }}>
                                        <Button
                                            {...styles.cancelButton}
                                            style={{ marginLeft: "0" }}
                                            type="button"
                                            data-test-selector="customerAddressForm_cancel"
                                            onClick={modalCloseHandler}
                                        >
                                            {translate("Close Window")}
                                        </Button>
                                    </GridItem>
                                </StyledWrapper>
                            )}
                            <StyledWrapper {...styles.question} as="h6">
                                <Icon src={CenQuestionIcon} {...questionIconStylessmall} />
                                <p>
                                    {translate("Question about shipping costs? Call us at")}&nbsp;
                                    <Link href="tel:18667035665" {...styles.linkStyles}>
                                        1-866-703-5665
                                    </Link>
                                </p>
                            </StyledWrapper>
                        </Modal>
                        <Typography as="p" {...styles.freightContent}>
                            <Link
                                href=""
                                onClick={openModalHandler}
                                onMouseOver={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                disabled={!variantSelectionCompleted && variantProductState.value?.isVariantParent}
                            >
                                {fields.freightContent}
                            </Link>
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
                {(fields?.phoneHeading || fields?.phoneContent) && (
                    <StyledWrapper {...styles.innerContainer}>
                        <Icon src={CenQuestionIcon} {...questionIconStyles} />
                        <StyledWrapper>
                            <Link href={`tel:${fields.phoneNo}`} {...linkStyles}>
                                {fields.phoneHeading}
                            </Link>
                            <Typography as="p" {...styles.contentStyles} style={{ color: fields.headingColor }}>
                                {fields.phoneContent}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.logisticWrapper}>
                {fields.logisticImage1 && (
                    <Img
                        src={fields.logisticImage1}
                        altText={fields.logisticImage1Alt}
                        {...styles.logisticImage1Style}
                    />
                )}
                {fields.logisticImage2 && (
                    <Img
                        src={fields.logisticImage2}
                        altText={fields.logisticImage2Alt}
                        {...styles.logisticImage2Style}
                    />
                )}
                {fields.logisticText && (
                    <StyledWrapper {...styles.logisticTextStyle}>{fields.logisticText}</StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProductContext(CenPdpFreightCalculatorContactBlock)),
    definition: {
        group: "Common",
        displayName: "CEN PDP Freight Block",
        fieldDefinitions: [
            {
                name: fields.freightlink,
                editorTemplate: "TextField",
                displayName: "Link",
                defaultValue: "Freight Calculator",
                fieldType: "General",
            },
            {
                name: fields.freightContent,
                editorTemplate: "TextField",
                displayName: "Content",
                defaultValue: "Check freight for your location",
                fieldType: "General",
            },
            {
                name: fields.freightContentColor,
                editorTemplate: "ColorPickerField",
                displayName: "Content",
                defaultValue: "#0F96D9",
                fieldType: "General",
            },
            {
                name: fields.headingColor,
                editorTemplate: "ColorPickerField",
                displayName: "Heading Color",
                defaultValue: "#636161",
                fieldType: "General",
            },
            {
                name: fields.phoneNo,
                editorTemplate: "TextField",
                displayName: "Contact No",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.phoneHeading,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.phoneContent,
                editorTemplate: "TextField",
                displayName: "Content",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.shippingRateBackgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Shipping Rate Background  Color",
                defaultValue: "#FAEECF",
                fieldType: "General",
            },
            {
                name: fields.logisticImage1,
                editorTemplate: "ImagePickerField",
                displayName: "Logistic Image 1",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.logisticImage1Alt,
                editorTemplate: "TextField",
                displayName: "Logistic Image 1 Alt Text",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.logisticImage2,
                editorTemplate: "ImagePickerField",
                displayName: "Logistic Image 2",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.logisticImage2Alt,
                editorTemplate: "TextField",
                displayName: "Logistic Image 2 Alt Text",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.logisticText,
                editorTemplate: "TextField",
                displayName: "Logistic Content",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
